import { useState, ChangeEvent, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { IKnmReasonChangeType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonChangeType';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmReasonChangeTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonChangeTypeSelectOption';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import {
  geCatalogsKnmPlanReasonChangeTypesSelectOptions,
  getCatalogKnmDocumentTypesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { KnmReasonChangeTypeOperationEnum } from 'APIPackage/CatalogsModule/enums/KnmReasonChangeTypeOperationEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { convertFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertFileToBase64';
import { PASS_SIGNATURE_VALIDATION, INVALID_DOCUMENT_SIGNATURE } from 'CorePackage/MainModule/constants/notices';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { KnmAttachments } from 'KnmPackage/KnmDocumentsModule/components/KnmAttachments';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { IKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDocumentTypeSelectOption';
import { convertSignFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertSignFileToBase64';
import { getPlanListPlan } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { planListLinkKnmToPlan } from '../actions/planListActions';
import { IKnmPlan } from '../interfaces/IKnmPlan';
import styles from './AddKnmModal.module.scss';

interface ITemplateAddKnmModalProps {
  visible: boolean;
  isErknm: boolean;
  isKnm?: boolean;
  onClose: () => void;
  onApply?: (selectedReason: IKnmReasonChangeType, documents: IKnmDocumentRequest) => void;
  isCreatePlan?: boolean;
}

interface IAddKnmModalStoreProps {
  knmPlan: IKnmPlan | null;
  knmPlanReasonChangeOptions: IKnmReasonChangeTypeSelectOption[];
  knmDocumentTypesSelectOptions: IKnmDocumentTypeSelectOption[];
}

const AddKnmModalWithoutStore = ({
  visible,
  isErknm,
  isKnm,
  onClose,
  onApply,
  knmDocumentTypesSelectOptions,
  knmPlan,
  knmPlanReasonChangeOptions,
  isCreatePlan,
}: ITemplateAddKnmModalProps & IAddKnmModalStoreProps) => {
  const [inputValue, setInputValue] = useState('');
  const [document, setDocument] = useState<File | null>(null);
  const [signs, setSigns] = useState<Array<File>>([]);
  const [selectValue, setSelectValue] = useState<IKnmReasonChangeTypeSelectOption | null>(null);
  const { uploadDocumentSignatures, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const disableButton = isErknm ? selectValue && document : knmPlan && inputValue;
  const attachmentType = knmDocumentTypesSelectOptions?.find(el => el.code === 'TYPE_LV');
  const knmPlanReasonIncludeOptions = knmPlanReasonChangeOptions.filter(
    option => option.operation === KnmReasonChangeTypeOperationEnum.INCLUDE,
  );

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_PLAN_REASON_CHANGE_TYPES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_DOCUMENT_TYPES));
  }, [dispatch]);

  const handleAfterClose = useCallback(() => {
    setInputValue('');
    setSelectValue(null);
    setDocument(null);
    setSigns([]);
  }, []);

  const handleSaveButton = useCallback(() => {
    if (knmPlan && inputValue) {
      dispatch(planListLinkKnmToPlan(knmPlan.id, inputValue));
    }
    onClose();
  }, [dispatch, inputValue, knmPlan, onClose]);

  const handleSaveModalButton = useCallback(async () => {
    if (onApply && isErknm && selectValue) {
      const reason = {
        id: selectValue.id,
        title: selectValue.title,
        operation: selectValue.operation,
        code: selectValue.code,
        enabled: selectValue.enabled,
        weight: selectValue.weight,
      };

      if (document && signs.length) {
        const convertedDocument = await convertFileToBase64(document);
        const signActions = signs.map(async sign => {
          const signText = await convertSignFileToBase64(sign);

          return signText;
        });
        const signTextArr = await Promise.all(signActions);
        const files = {
          document: convertedDocument,
          signatures: signTextArr,
        };
        const res = await uploadDocumentSignatures(files);

        if (res && res.signed) {
          const attachment = await uploadKnmDocumentAttachment(document);

          dispatch(
            addNoticeAction({
              renderContent: PASS_SIGNATURE_VALIDATION,
              noticeType: NoticeTypesEnum.SUCCESS,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );
          if (attachmentType && res.signatureInfo && attachment) {
            const signedDocument: IKnmAttachment = {
              guid: attachment.guid || null,
              dateCreate: attachment.dateCreate || null,
              fileName: attachment.fileName || null,
              filePath: attachment.filePath || null,
              fileSize: attachment.fileSize,
              mimeType: attachment.mimeType || null,
              bucketName: attachment.bucketName || null,
              correlationToken: attachment.correlationToken || null,
              hash: attachment.hash || null,
              signed: true,
              signatureInfo: res.signatureInfo,
            };

            onApply(reason, { type: attachmentType, attachments: [signedDocument] });
          }
        } else {
          dispatch(
            addNoticeAction({
              renderContent: INVALID_DOCUMENT_SIGNATURE,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );
        }
      } else if (document && attachmentType) {
        const attachments = await uploadKnmDocumentAttachment(document);

        attachments && onApply(reason, { type: attachmentType, attachments: [attachments] });
      }
    } else {
      handleSaveButton();
    }
  }, [
    attachmentType,
    dispatch,
    document,
    handleSaveButton,
    isErknm,
    onApply,
    selectValue,
    signs,
    uploadDocumentSignatures,
    uploadKnmDocumentAttachment,
  ]);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value), []);

  const handleChangeSelect = useCallback((value: IKnmReasonChangeTypeSelectOption) => setSelectValue(value), []);

  const handleRemoveDocument = useCallback(() => {
    setDocument(null);
  }, []);

  const handleRemoveSign = useCallback(
    (index: number) => () => {
      setSigns(prev => prev.filter((el: File, idx: number) => idx !== index));
    },
    [],
  );

  const handleSetAttachment = useCallback((file: File, isSign: boolean) => {
    isSign ? setSigns(prev => [...prev, file]) : setDocument(file);
  }, []);

  if (!knmPlan && !isCreatePlan) {
    return null;
  }

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={visible} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title="Добавление КНМ в план" sizeText="small" color="black" />
      <ModalBody size="content" border="bottom">
        <h4 className={styles.Text}>
          {!isKnm && knmPlan
            ? `План КНМ: ${knmPlan.id} ${knmPlan.knoName} ${knmPlan.prosecName} ${knmPlan.startDate} - ${knmPlan.endDate}`
            : 'Основание включения'}
        </h4>
        <div className={styles.InputBlock}>
          <div className={styles.InputContainer}>
            {!isKnm && (
              <TextInput
                className={styles.Input}
                placeholder="Введите учетный номер КНМ"
                onChange={handleChangeInput}
                value={inputValue}
              />
            )}
            {isErknm && (
              <>
                <SelectInput
                  className={styles.Select}
                  placeholder="Тип"
                  options={knmPlanReasonIncludeOptions}
                  size="medium"
                  value={selectValue}
                  onChange={handleChangeSelect}
                />
                <KnmAttachments
                  document={document}
                  signs={signs}
                  handleRemoveDocument={handleRemoveDocument}
                  handleRemoveSign={handleRemoveSign}
                  handleSetAttachment={handleSetAttachment}
                />
              </>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button
          text="Сохранить"
          id="saveButton"
          size="medium"
          disabled={!disableButton}
          onClick={handleSaveModalButton}
        />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IAddKnmModalStoreProps => ({
  knmPlan: getPlanListPlan(state),
  knmPlanReasonChangeOptions: geCatalogsKnmPlanReasonChangeTypesSelectOptions(state),
  knmDocumentTypesSelectOptions: getCatalogKnmDocumentTypesSelectOptions(state),
});

export const AddKnmModal = connect(mapStateToProps)(AddKnmModalWithoutStore);
